/**
 * How a commitment reads, wherever it is drawn: its list, its page, the peek.
 *
 * Every word and tone for a commitment's type, status, priority, coverage and
 * due date lives here, so the list and the page cannot say one thing two ways.
 */
import type { Tone } from '@/components/mono/primitives';
import { formatDate as formatLocaleDate } from '@/i18n/formatters';
import type {
  CommitmentCoverage,
  CommitmentPriority,
  CommitmentStatus,
  CommitmentType,
  CommitmentWithCoverage,
} from '@/types/prioritization/commitment';

export const COMMITMENT_TYPE_LABELS: Record<CommitmentType, string> = {
  customer: 'Customer',
  partner: 'Partner',
  regulatory: 'Regulatory',
  internal: 'Internal',
};

export const COMMITMENT_STATUS_LABELS: Record<CommitmentStatus, string> = {
  active: 'Active',
  fulfilled: 'Fulfilled',
  expired: 'Expired',
  cancelled: 'Cancelled',
  suspended: 'Suspended',
  superseded: 'Superseded',
};

export const COMMITMENT_STATUS_TONE: Record<CommitmentStatus, Tone> = {
  active: 'green',
  fulfilled: 'slate',
  expired: 'neutral',
  cancelled: 'coral',
  suspended: 'mustard',
  superseded: 'slate',
};

/**
 * The statuses a status edit may set (update_commitment_status). 'superseded' is the
 * Supersede act's alone (983), after a continuity ruling, so the edit never offers it.
 */
export const EDITABLE_COMMITMENT_STATUSES: CommitmentStatus[] = ['active', 'fulfilled', 'expired', 'cancelled'];

export const COMMITMENT_PRIORITY_LABELS: Record<CommitmentPriority, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

export const COMMITMENT_PRIORITY_TONE: Record<CommitmentPriority, Tone> = {
  critical: 'coral',
  high: 'mustard',
  medium: 'mustard',
  low: 'neutral',
};

/** Sorts critical first. */
export const COMMITMENT_PRIORITY_RANK: Record<CommitmentPriority, number> = { critical: 0, high: 1, medium: 2, low: 3 };

export const COMMITMENT_COVERAGE: Record<CommitmentCoverage, { label: string; tone: Tone; detail: string }> = {
  covered: { label: 'Covered', tone: 'green', detail: 'All linked items are in an active cycle.' },
  partial: { label: 'Partial', tone: 'mustard', detail: 'Some linked items are not in an active cycle.' },
  'at-risk': { label: 'At risk', tone: 'coral', detail: 'No linked items are in any active cycle.' },
  'no-items': { label: 'No items', tone: 'neutral', detail: 'No items linked to this commitment yet.' },
};

/** "1 of 2 linked items in an active cycle", or nothing when none are linked. */
export function coverageCountLine(c: Pick<CommitmentWithCoverage, 'coveredItemCount' | 'linkedItemCount'>): string | null {
  if (c.linkedItemCount === 0) return null;
  return `${c.coveredItemCount} of ${c.linkedItemCount} linked item${c.linkedItemCount === 1 ? '' : 's'} in an active cycle.`;
}

/** Active, and no linked item in an active cycle: the promise has nothing behind it. */
export function isUncovered(c: Pick<CommitmentWithCoverage, 'status' | 'coverage'>): boolean {
  return c.status === 'active' && (c.coverage === 'at-risk' || c.coverage === 'no-items');
}

/** Whole days from today to the due date; negative when past. Null without a date. */
export function daysUntilDue(dueDate: string | null, today: Date = new Date()): number | null {
  if (!dueDate) return null;
  const start = new Date(today);
  start.setHours(0, 0, 0, 0);
  const due = new Date(`${dueDate}T00:00:00`);
  // Rounded, not floored: across a clock change a day is 23 or 25 hours long.
  return Math.round((due.getTime() - start.getTime()) / 86_400_000);
}

export function isDueWithin30Days(dueDate: string | null): boolean {
  const d = daysUntilDue(dueDate);
  return d != null && d >= 0 && d <= 30;
}

/** Past due reads coral, due within 30 days mustard, otherwise quiet. */
export function dueTone(dueDate: string | null): Tone {
  const d = daysUntilDue(dueDate);
  if (d == null) return 'neutral';
  if (d < 0) return 'coral';
  if (d <= 30) return 'mustard';
  return 'neutral';
}

export function formatDueDate(dueDate: string | null): string {
  if (!dueDate) return '—';
  return formatLocaleDate(new Date(`${dueDate}T00:00:00`), { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "Due in 12 days", "Due today", "3 days overdue", or "No due date". */
export function dueLine(dueDate: string | null): string {
  const d = daysUntilDue(dueDate);
  if (d == null) return 'No due date';
  if (d === 0) return 'Due today';
  if (d < 0) return `${-d} day${d === -1 ? '' : 's'} overdue`;
  return `Due in ${d} day${d === 1 ? '' : 's'}`;
}
